import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from 'dux/app/hooks';

import { legacyTheme, styled } from '@prose-ui/legacy';

import TrayCard from 'Components/TrayCard';

import { FRAGRANCE_FREE } from 'constants/skincareFragrances';

import Loading from 'Blocks/Loading';

import ErrorContainer from 'Containers/ErrorContainer';

import consultationCategoryProp from 'PropTypes/consultationProps';

import QuestionLayout from '../Scenes/QuestionLayout';

import * as consultationSelectors from 'dux/consultation/selectors';
import { fetchSkincareFragrances } from 'dux/consultation/thunks';

const FragrancesWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin-top: 32px;
  column-gap: 16px;
  ${legacyTheme.breakpoints.up('md')} {
    margin-top: 50px;
  }
`;

const FragranceWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-basis: ${({ fullRow }) => fullRow && '100%'};
`;

const useSkincareFragrances = () => {
  const dispatch = useDispatch();
  const status = useSelector(consultationSelectors.getSkincareFragrancesStatus);
  const fragrancesList = useSelector(consultationSelectors.getSkincareFragrancesDetails);
  const error = useSelector(consultationSelectors.getSkincareFragrancesError);

  const fetchFragrances = () => dispatch(fetchSkincareFragrances());

  useEffect(() => {
    fetchFragrances();
  }, []);

  return { error, fetchFragrances, fragrancesList, status };
};

const SkincareFragrance = ({
  consultationCategory,
  initialValue,
  question,
  questionIndex,
  onNext,
}) => {
  const { error, fetchFragrances, fragrancesList, status } = useSkincareFragrances();
  const [selectedFragrance, setSelectedFragrance] = useState();
  const onClickOption = value => {
    if (value === FRAGRANCE_FREE) {
      setSelectedFragrance({ pref_fragrance_skin: null, pref_fragrance_free_skin: true });
    } else {
      setSelectedFragrance({ pref_fragrance_skin: value, pref_fragrance_free_skin: false });
    }
  };
  const submit = () => onNext(selectedFragrance);
  const { subtitle, title, tip } = question;

  useEffect(() => {
    setSelectedFragrance(initialValue);
  }, [initialValue]);

  if (status === 'error') {
    return (
      <ErrorContainer
        error={error}
        retry={fetchFragrances}
        sentryMessage="Invalid state on SkincareFragrance"
      />
    );
  }

  return (
    <QuestionLayout
      consultationCategory={consultationCategory}
      disabled={
        !selectedFragrance?.pref_fragrance_skin && !selectedFragrance?.pref_fragrance_free_skin
      }
      display="flex"
      justifyContent="start"
      questionIndex={questionIndex}
      submit={submit}
      subtitle={subtitle}
      tipContent={tip}
      title={title}
    >
      {status === 'idle' || status === 'loading' ? (
        <Loading />
      ) : (
        <FragrancesWrapper>
          {fragrancesList?.map((fragrance, index) => {
            const isSelected =
              fragrance.slug === FRAGRANCE_FREE
                ? selectedFragrance?.pref_fragrance_free_skin === true
                : selectedFragrance?.pref_fragrance_skin === fragrance.slug;
            return (
              <FragranceWrapper
                key={fragrance.slug}
                fullRow={(index === 0 && fragrance.recommended) || fragrancesList?.length === 2}
              >
                <TrayCard
                  description={fragrance.description}
                  details={fragrance.details}
                  hasImageBorder
                  imageAlt={fragrance.imageAlt}
                  imageUrl={fragrance.urlsByAssetName?.fragFreeBottleImg ?? fragrance.url}
                  isRecommended={fragrance.recommended}
                  isSkincareConsultation
                  label={fragrance.name}
                  onClick={() => onClickOption(fragrance.slug)}
                  recommendedReason={fragrance.recommended && fragrance.reasonWhyLabel}
                  selected={isSelected}
                />
              </FragranceWrapper>
            );
          })}
        </FragrancesWrapper>
      )}
    </QuestionLayout>
  );
};

SkincareFragrance.propTypes = {
  consultationCategory: consultationCategoryProp.isRequired,
  initialValue: PropTypes.shape({
    pref_fragrance_free_skin: PropTypes.bool,
    pref_fragrance_skin: PropTypes.string,
  }),
  question: PropTypes.shape({
    tip: PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string,
    }),
    title: PropTypes.string,
    subtitle: PropTypes.string,
  }).isRequired,
  questionIndex: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
};

SkincareFragrance.defaultProps = {
  initialValue: {},
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    question: consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  })
)(SkincareFragrance);
